import some from "lodash/some";
import { useActivityShowContext } from "PFApp/activities/show/context/activity_show_context";
import { useActivityPageConversations } from "PFApp/activities/show/hooks/use_activity_page_conversations";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { ShowNewChatModal } from "PFApp/conversations/show_new_chat_modal/show_new_chat_modal";
import useIsChatEnabled from "PFApp/use_is_chat_enabled";
import { ActionIcon } from "PFComponents/action_icon";
import { Inline, Stack } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { Tile } from "PFComponents/tile";
import { Typography } from "PFComponents/typography";
import { useInvites } from "PFCore/hooks/queries/invites/use_invites";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ActivityOverviewChatItem } from "./activity_overview_chat_item";

export const ActivityOverviewChats = () => {
  const { t } = useTranslation("activities", { keyPrefix: "show.details.chats" });
  const activity = useCurrentActivity();
  const { isEngagement } = useActivityShowContext();
  const isChatEnabled = useIsChatEnabled();
  const { primaryConversation, otherConversations, participantsOfConversations } =
    useActivityPageConversations(activity);
  const { data: invites } = useInvites(activity.id);

  const [showNewChatModal, setShowNewChatModal] = useState(false);

  if (!isChatEnabled || activity.state === "draft") {
    return null;
  }

  const allParticipants = (primaryConversation && participantsOfConversations[primaryConversation.id]) || [];
  const hasInvites = !invites?.length;
  const hasOwners = !activity?.owners.length;
  const hasParticipants = some(allParticipants, [{ kind: "owner" }]);

  const showMainChat =
    allParticipants &&
    primaryConversation &&
    (activity.private ? hasOwners || hasInvites || hasParticipants : activity.communication_type === "group");

  const showOtherChats = !isEngagement && (activity.coowned || !otherConversations?.length);

  if (!showMainChat && !showOtherChats) {
    return null;
  }

  const chatCount = (showMainChat ? 1 : 0) + ((showOtherChats && otherConversations?.length) || 0);

  return (
    <Tile variant="light" paddingVariant="lg" qaId="ActivityDetailsChats">
      <Stack>
        <Inline spread alignItems="center">
          <Inline>
            <Icon name="chat" />
            <Typography variant="h5">{t("title", { count: chatCount })}</Typography>
          </Inline>
          {showOtherChats && activity.coowned && (
            <ActionIcon
              name="add"
              size="sm"
              onClick={() => setShowNewChatModal(true)}
              qaId={"ActivityDetailsChatStartButton"}
            />
          )}
        </Inline>
        {showMainChat && (
          <ActivityOverviewChatItem
            conversation={primaryConversation}
            participants={allParticipants}
            isLast={!showOtherChats}
          />
        )}
        {showOtherChats &&
          otherConversations?.map((conversation, index) => (
            <ActivityOverviewChatItem
              key={conversation.id}
              conversation={conversation}
              participants={participantsOfConversations[conversation.id] || []}
              isLast={index === otherConversations.length - 1}
            />
          ))}
      </Stack>
      {showNewChatModal && (
        <ShowNewChatModal activity={activity} handleClose={() => setShowNewChatModal(false)} />
      )}
    </Tile>
  );
};
